import Button from '@/components/UI/Button';
import { _isNil, _notNil } from '@/littledash';
import type { Finding } from '@/model/Agent.model';
import { useState, type FC } from 'react';
import SendCellValue from './SendCellValue';
import SendRuleId from './SendRuleId';
import SendFdaValidatorIds from './SendFdaValidatorIds';
import SendFindingSeverity from './SendFindingSeverity';
import { encodingIdOf, fdaValidatorIdsOf } from '@/components/Agents/SendValidator/utils/SendSeverity';
import {
  hasUnlabelledSuggestion,
  IN_VIVO_AI_SHORT,
  suggestionOriginLabelOf,
} from '@/components/Agents/SendValidator/utils/SendAiUse';
import { findingCategoryLabel } from '@/components/Agents/SendValidator/utils/SendText';
import styles from './SendResults.module.scss';

const PAGE_SIZE = 25;

/** What a field the result does not carry reads as, so an absent value is never mistaken for a pass. */
export const NOT_AVAILABLE = 'Not available';

interface SendFindingsTableProps {
  /** What the checks found, in display order. Never notices or Benchling In Vivo AI notes. */
  findings: ReadonlyArray<Finding>;
  /**
   * Whether Benchling In Vivo AI took part in the run. Only read for a result stored before each
   * suggestion said who made it, where a run-level caveat is all there is.
   */
  aiUsed: boolean;
  testId: string;
}

/**
 * Builds a React key for a finding. Findings carry no server id, so the key combines the finding's
 * identifying fields with the absolute list index, which disambiguates findings whose other fields
 * coincide.
 */
const findingKey = (finding: Finding, index: number): string =>
  `${finding.rule?.id ?? ''}-${finding.column_name ?? ''}-${finding.row_number ?? ''}-${index}`;

/** Who made a suggestion, under it; nothing for a finding with none or an older result's. */
const SuggestionOrigin: FC<{ finding: Finding }> = ({ finding }) => {
  const label = suggestionOriginLabelOf(finding);
  if (_isNil(label)) {
    return null;
  }
  return (
    <span
      className={styles.cellNote}
      data-test-element="finding-suggestion-origin"
      data-test-key={finding.suggestion_origin ?? undefined}
    >
      {label}
    </span>
  );
};

/**
 * The checks' findings for one file, a page at a time: severity, the rule it comes from and the kind
 * of check, where it is, the value found, a suggested value and what it says.
 *
 * "Row" is the data row in the uploaded file, labelled as such until findings carry a record's
 * identifier. "Suggestion" is a suggestion only, never the value the rule requires, and each says who
 * made it: the validator's closest match, or the AI review where the validator found none. A result
 * stored before suggestions said so gets the column-level caveat instead.
 */
const SendFindingsTable: FC<SendFindingsTableProps> = ({ findings, aiUsed, testId }) => {
  const [page, setPage] = useState(0);
  const pageCount = Math.max(1, Math.ceil(findings.length / PAGE_SIZE));
  const safePage = Math.min(page, pageCount - 1);
  const pageFindings = findings.slice(safePage * PAGE_SIZE, safePage * PAGE_SIZE + PAGE_SIZE);
  const unlabelled = aiUsed && hasUnlabelledSuggestion(findings);

  return (
    <div data-test-component="SendFindingsTable" data-test-element="findings-grid">
      <div className={styles.gridScroll}>
        <table className={styles.grid} data-testid={testId}>
          <thead>
            <tr>
              <th scope="col">Severity</th>
              <th scope="col">Rule</th>
              <th scope="col">Column</th>
              <th scope="col" title="The data row in the uploaded file" data-test-element="row-header">
                Row
                <span className={`${styles.headerNote} ${styles.headerNoteShort}`}>in the file</span>
              </th>
              <th scope="col">Value</th>
              <th scope="col" data-test-element="suggestion-header">
                Suggestion
                {unlabelled && (
                  <span className={styles.headerNote} data-test-element="suggestion-ai-caveat">
                    Some suggestions come from {IN_VIVO_AI_SHORT} review
                  </span>
                )}
              </th>
              <th scope="col">Message</th>
            </tr>
          </thead>
          <tbody>
            {pageFindings.map((finding, index) => {
              const absoluteIndex = safePage * PAGE_SIZE + index;
              return (
                <tr key={findingKey(finding, absoluteIndex)} data-test-element="finding-row">
                  <td>
                    <SendFindingSeverity finding={finding} />
                  </td>
                  <td className={styles.findingRule} data-test-element="finding-rule">
                    {_notNil(finding.rule) ? (
                      <SendRuleId
                        id={finding.rule.id}
                        publisher={finding.rule.publisher}
                        encodingId={encodingIdOf(finding)}
                        markPartial
                      />
                    ) : (
                      NOT_AVAILABLE
                    )}
                    <span className={styles.cellNote} data-test-element="finding-category">
                      {findingCategoryLabel(finding.category)}
                    </span>
                    <SendFdaValidatorIds ids={fdaValidatorIdsOf(finding)} />
                  </td>
                  <td>{finding.column_name ?? '—'}</td>
                  <td>{finding.row_number ?? '—'}</td>
                  <td data-test-element="finding-current">
                    {_notNil(finding.current_value) ? <SendCellValue value={finding.current_value} /> : '—'}
                  </td>
                  <td data-test-element="finding-suggestion">
                    {_notNil(finding.expected_value) ? <SendCellValue value={finding.expected_value} /> : '—'}
                    <SuggestionOrigin finding={finding} />
                  </td>
                  <td>{finding.message}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {pageCount > 1 && (
        <div className="flex items-center mt3" data-test-element="pagination">
          <Button
            outline
            disabled={safePage === 0}
            onClick={() => setPage(safePage - 1)}
            testId="invivo-send-validator__grid-prev"
          >
            Previous
          </Button>
          <span className="f6 dark-gray mh3" data-testid="invivo-send-validator__grid-page">
            Page {safePage + 1} of {pageCount}
          </span>
          <Button
            outline
            disabled={safePage >= pageCount - 1}
            onClick={() => setPage(safePage + 1)}
            testId="invivo-send-validator__grid-next"
          >
            Next
          </Button>
        </div>
      )}
    </div>
  );
};

export default SendFindingsTable;
